import { ROLES_CPA_DELEGADO, ROLES_CPA_DIRECTIVA_GESTION, } from "./courseRoles";
/** Código reservado del Centro de Padres y Apoderados (grupo especial, no curso con pupilo). */
export const CPA_COURSE_CODE = "CPA";
/** Grupo virtual de funcionarios del colegio (portería, inspectores, etc.). */
export const FUNCIONARIOS_COURSE_CODE = "FUNCIONARIOS";
/** Grupo virtual de usuarios de kiosco escolar. */
export const KIOSK_COURSE_CODE = "KIOSK";
export const isFuncionariosCourse = (course) => {
    if (!course)
        return false;
    if (course.entity === "funcionarios")
        return true;
    const code = String(course.course_code ?? course.course_id ?? "").toUpperCase();
    return code === FUNCIONARIOS_COURSE_CODE;
};
export const ALUMNOS_COURSE_CODE = "ALUMNOS";
export const isAlumnosCourse = (course) => {
    if (!course)
        return false;
    if (course.entity === "alumnos")
        return true;
    const code = String(course.course_code ?? course.course_id ?? "").toUpperCase();
    return code === ALUMNOS_COURSE_CODE;
};
export const isKioskCourse = (course) => {
    if (!course)
        return false;
    if (course.entity === "kiosk")
        return true;
    const code = String(course.course_code ?? course.course_id ?? "").toUpperCase();
    return code === KIOSK_COURSE_CODE;
};
export const isCpaCourse = (course) => {
    if (!course)
        return false;
    if (course.entity === "cpa")
        return true;
    const code = String(course.course_code ?? course.course_id ?? "").toUpperCase();
    return code === CPA_COURSE_CODE;
};
/** Grupos sin pupilo: CPA, FUNCIONARIOS, KIOSK y grupos con member_representation=self. */
export const courseRequiresStudentName = (course) => {
    if (!course)
        return false;
    if (isCpaCourse(course) ||
        isFuncionariosCourse(course) ||
        isKioskCourse(course) ||
        isAlumnosCourse(course)) {
        return false;
    }
    const representation = String(course.member_representation ?? "")
        .trim()
        .toLowerCase();
    if (representation === "self")
        return false;
    if (representation === "pupils")
        return true;
    // Legacy: sin campo explícito, cursos de apoderado requieren student_name
    return true;
};
/** Kiosco disponible para grupos con pupilos, directiva CPA y operadores KIOSK. */
export const canAccessKioskModule = (course) => isKioskCourse(course) ||
    isCpaDirectiva(course) ||
    courseRequiresStudentName(course);
/** Los retiros de alumnos solo aplican a grupos que representan pupilos. */
export const canAccessGuardianWithdrawals = (course) => courseRequiresStudentName(course);
/**
 * Curso con gestión de pupilos (listado / campo pupilo).
 * Prioriza member_representation; si no existe, usa heurística de curso académico
 * de colegio (excluye G* genéricos).
 */
export const isAcademicSchoolCourse = (course) => {
    if (!courseRequiresStudentName(course))
        return false;
    const representation = String(course?.member_representation ?? "")
        .trim()
        .toLowerCase();
    if (representation === "pupils")
        return true;
    const code = String(course?.course_code ?? course?.course_id ?? "")
        .trim()
        .toUpperCase();
    if (!code || code.startsWith("G"))
        return false;
    const entity = String(course?.entity ?? "course").toLowerCase();
    return entity === "course";
};
/**
 * Copy "curso" vs "grupo": colegio académico usa "curso";
 * extraescolar (G*), CPA, funcionarios y kiosco usan "grupo".
 */
export const usesCourseNoun = (course) => {
    if (!course)
        return false;
    if (isCpaCourse(course) ||
        isFuncionariosCourse(course) ||
        isKioskCourse(course)) {
        return false;
    }
    const code = String(course.course_code ?? course.course_id ?? "")
        .trim()
        .toUpperCase();
    if (!code || code.startsWith("G"))
        return false;
    return true;
};
/**
 * Apoderados adicionales solo en cursos académicos de colegio con pupilo.
 * No aplica a CPA, funcionarios, kiosco, clubes ni a quien ya es usuario extra.
 */
export const canAssociateExtraGuardians = (course) => {
    if (!course)
        return false;
    if (course.isExtraGuardian === true)
        return false;
    if (!courseRequiresStudentName(course))
        return false;
    const entity = String(course.entity ?? "course").toLowerCase();
    return entity === "course";
};
/**
 * Directiva CPA con gestión/menú completo: curso CPA +
 * president, vicepresident, treasurer, secretary o director.
 * Los delegados NO entran aquí (ver `isCpaDelegado`).
 */
export const isCpaDirectiva = (course) => {
    if (!isCpaCourse(course))
        return false;
    const roles = course?.roles ?? [];
    return ROLES_CPA_DIRECTIVA_GESTION.some((role) => roles.includes(role));
};
/**
 * Delegado del CPA sin cargo de gestión: solo acceso mínimo (Mi cuenta).
 * Si además tiene un rol de gestión, prevalece `isCpaDirectiva`.
 */
export const isCpaDelegado = (course) => {
    if (!isCpaCourse(course))
        return false;
    if (isCpaDirectiva(course))
        return false;
    const roles = course?.roles ?? [];
    return ROLES_CPA_DELEGADO.some((role) => roles.includes(role));
};
const resolveFuncionarioCargoLabel = (course) => {
    const label = String(course.cargo_label ?? "").trim();
    if (label)
        return label;
    const code = String(course.cargo_code ?? "").trim().toUpperCase();
    const fallback = {
        GATE_GUARD: "Portería",
        INSPECTOR: "Inspectoría",
        INSPECTOR_GENERAL: "Inspectoría general",
        SCHOOL_ADMIN: "Administración escolar",
        RECTOR: "Rectoría",
        DOCENTE: "Docente",
        DRIVER: "Chofer",
    };
    return fallback[code] ?? "";
};
/** Título principal del selector de grupo (header y listado). */
export const getGroupSelectorTitle = (course) => {
    if (!course)
        return "";
    if (isFuncionariosCourse(course)) {
        const cargo = resolveFuncionarioCargoLabel(course);
        return cargo ? `Funcionarios · ${cargo}` : "Funcionarios";
    }
    if (isKioskCourse(course))
        return "Usuarios de kiosco";
    if (isCpaCourse(course))
        return "CPA";
    if (course.course_id?.startsWith("G")) {
        return course.course_name || course.course_id;
    }
    const code = String(course.course_code ?? course.course_id ?? "");
    if (!code)
        return course.course_name || "";
    try {
        return parseCourseCode(code).shortName;
    }
    catch {
        return course.course_name || code;
    }
};
/** Subtítulo del selector: pupilo solo cuando el grupo usa representación de pupilos. */
export const getGroupSelectorSubtitle = (course) => {
    if (!course)
        return "";
    if (isFuncionariosCourse(course)) {
        return String(course.school_name ?? course.school_id ?? "").trim();
    }
    if (isKioskCourse(course)) {
        return String(course.school_name ?? course.school_id ?? "").trim();
    }
    if (isCpaCourse(course)) {
        return String(course.school_name ?? course.school_id ?? "").trim();
    }
    if (courseRequiresStudentName(course)) {
        return String(course.student_name ?? course.school_name ?? "").trim();
    }
    return String(course.school_name ?? course.school_id ?? "").trim();
};
export const parseCourseCode = (code) => {
    if (!code || typeof code !== "string") {
        console.error("[parseCourseCode] Código inválido:", code);
        throw new Error("Código de curso inválido");
    }
    if (code === "FAKE") {
        return {
            shortName: "Curso TEST",
            longName: "Curso TEST",
        };
    }
    if (code === "TLC") {
        return {
            shortName: "Todos los cursos",
            longName: "Todos los cursos",
        };
    }
    if (code === "CPA") {
        return {
            shortName: code,
            longName: code,
        };
    }
    if (code.toUpperCase() === FUNCIONARIOS_COURSE_CODE) {
        return {
            shortName: "Funcionarios",
            longName: "Funcionarios",
        };
    }
    if (code.toUpperCase() === KIOSK_COURSE_CODE) {
        return {
            shortName: "Usuarios de kiosco",
            longName: "Usuarios de kiosco",
        };
    }
    const upperCode = code.toUpperCase();
    let base;
    let letter;
    // Manejar diferentes patrones de códigos
    if (upperCode.match(/^K[A-Z]$/)) {
        // Kinder: K + letra (ej: KA, KB, KC...)
        base = "K";
        letter = upperCode.slice(1);
    }
    else if (upperCode.match(/^PK[A-Z]$/)) {
        // Prekinder: PK + letra (ej: PKA, PKB...)
        base = "PK";
        letter = upperCode.slice(2);
    }
    else if (upperCode.match(/^[0-9]+[BM][A-Z]$/)) {
        // Básico/Medio: número + nivel + letra (ej: 1BA, 2MB...)
        const match = upperCode.match(/^([0-9]+[BM])([A-Z])$/);
        if (match) {
            base = match[1];
            letter = match[2];
        }
        else {
            console.error("[parseCourseCode] Formato no reconocido:", code);
            return {
                shortName: code,
                longName: code,
            };
        }
    }
    else {
        console.error("[parseCourseCode] Formato no reconocido:", code);
        return {
            shortName: code,
            longName: code,
        };
    }
    const levelMap = {
        PK: "Prekinder",
        K: "Kinder",
        B: "Básico",
        M: "Medio",
    };
    const ordinalMap = {
        1: "Primero",
        2: "Segundo",
        3: "Tercero",
        4: "Cuarto",
        5: "Quinto",
        6: "Sexto",
        7: "Séptimo",
        8: "Octavo",
    };
    let shortName = "";
    let longName = "";
    if (base === "PK" || base === "K") {
        const name = levelMap[base];
        shortName = name + " " + letter;
        longName = name + " " + letter;
    }
    else {
        const levelChar = base.slice(-1); // B o M
        const numberPart = base.slice(0, -1); // 1-8 o 1-4
        if (!["B", "M"].includes(levelChar)) {
            console.error("[parseCourseCode] Nivel no reconocido:", levelChar);
            throw new Error("Nivel de curso no reconocido");
        }
        const level = levelMap[levelChar];
        const number = ordinalMap[parseInt(numberPart)];
        if (!number) {
            console.error("[parseCourseCode] Número de curso inválido:", numberPart);
            throw new Error("Número de curso inválido");
        }
        shortName = `${numberPart}° ${level} ${letter}`;
        longName = `${number} ${level} ${letter}`;
    }
    return {
        shortName,
        longName,
    };
};
